import { AlertCircle, Check, Info, RefreshCw, Users } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Badge, Button, Callout, Card, EmptyState, Field, Modal, inputCls } from '../../components/ui';
import {
  DirectoryError,
  type Envelope,
  type Expected,
  type Preview,
  type Registration,
  command,
  dateTime,
  directoryRequest,
} from '../../lib/personnel-api';

export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  if (!error) return null;
  const e = error instanceof DirectoryError ? error : null;
  return (
    <Callout tone="bad" icon={AlertCircle}>
      <p className="font-medium">
        {e?.status === 403
          ? 'Нет доступа к этим сведениям'
          : e?.status === 409
            ? 'Данные изменились'
            : 'Не удалось выполнить действие'}
      </p>
      <p>{e?.message ?? 'Повторите запрос. Введённые данные сохранены.'}</p>
      {e?.requestId && <p className="mt-1 text-xs">Код обращения: {e.requestId}</p>}
      {retry && (
        <Button className="mt-2" icon={RefreshCw} onClick={retry}>
          Обновить данные
        </Button>
      )}
    </Callout>
  );
}
export function Loading() {
  return (
    <p role="status" className="p-5 text-sm text-ink-3">
      Загрузка справочника…
    </p>
  );
}
export function NoResults({
  children = 'Попробуйте изменить поиск или фильтры.',
  title = 'Ничего не найдено',
}: { children?: string; title?: string }) {
  return <EmptyState icon={Users} title={title} description={children} />;
}
export function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="border-b border-line pb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}
const CASE_REASON_LABEL: Record<string, string> = {
  source_instance_unknown: 'Не подтверждён экземпляр базы-источника',
  upload_origin_unknown: 'Не подтверждён источник выгрузки персонала',
  candidate_set_too_broad: 'Слишком много кандидатов для автоматической сверки — нужен ручной разбор',
  candidate_input_changed: 'Исходные сведения изменились — требуется повторная сверка',
  candidate_build_pending: 'Сверка между площадками подготавливается',
  accepted_inputs_pending: 'Принятые исходные сведения ещё обрабатываются',
  accepted_inputs_failed: 'Не удалось обработать принятые исходные сведения',
  mapping_target_missing: 'Не найдена карточка для подтверждённой связи',
  explicit_human_basis_required: 'Нужно подтвердить личный аккаунт человека и указать основание',
  disabled_account_requires_manual_binding: 'Для отключённого аккаунта требуется ручная проверка привязки',
  duplicate_manifest_mapping: 'В перечне повторяется привязка человека или аккаунта',
  account_binding_conflict: 'Есть противоречащие друг другу привязки аккаунта и человека',
  duplicate_source_key: 'Повторяется ссылка на запись источника',
  target_not_visible: 'Нет доступа к связанной карточке',
  person_reference_missing: 'Не указана подтверждённая ссылка на человека',
  explicit_reference_missing: 'Не хватает явной ссылки для подтверждения связи',
  brigade_reference_missing: 'Не указана подтверждённая ссылка на бригаду',
  stable_registration_missing: 'Не найдена устойчивая регистрация человека на площадке',
  period_overlap: 'Пересекаются периоды связей или членства',
  brigade_inactive_period: 'Членство выходит за период деятельности бригады',
  ambiguous_names: 'ФИО допускают несколько вариантов сопоставления',
  status_conflict: 'Сведения о статусе сотрудника расходятся',
  multiple_confirmed_people: 'Регистрации связаны с разными подтверждёнными людьми',
  same_site_collision: 'На одной площадке есть несколько подходящих регистраций',
  identity_dispute: 'Открыт спор о принадлежности регистрации',
  period_boundary: 'Нужно уточнить период действия связи',
  manual_decision: 'Есть ранее принятое ручное решение',
  automatic_undo: 'Предыдущее автоматическое сопоставление отменено',
  source_not_proven: 'Происхождение регистрации не подтверждено',
  person_dependencies: 'Есть связанные данные, требующие проверки перед объединением',
  incomplete_history: 'Недостаточно сведений об истории регистрации',
};
export function caseReason(reasonCode: string, message?: string) {
  return (
    CASE_REASON_LABEL[reasonCode] ??
    (message && message !== reasonCode && !/^[a-z][a-z0-9_]*$/.test(message)
      ? message
      : 'Причина требует дополнительного разбора')
  );
}
export function StateBadge({ state }: { state?: string }) {
  const labels: Record<string, string> = {
    provisional: 'Предварительная',
    confirmed: 'Подтверждена',
    joined: 'Объединена',
    active: 'Действует',
    archived: 'В архиве',
    compatible: 'Совместимые ФИО',
    conflict: 'Требует разбора',
    deferred: 'Отложена',
    reviewed: 'Рассмотрена',
    automatic: 'Сопоставлены автоматически',
    dismissed: 'Уволен',
    unknown: 'Статус неизвестен',
    status_conflict: 'Статус расходится',
    open: 'Открыт',
    resolved: 'Устранён',
    blocked: 'Заблокирован',
  };
  return (
    <Badge
      tone={
        state === 'conflict' || state === 'status_conflict'
          ? 'warn'
          : state === 'confirmed' || state === 'active' || state === 'resolved' || state === 'automatic'
            ? 'ok'
            : 'neutral'
      }
    >
      {labels[state ?? ''] ?? state ?? 'Не указан'}
    </Badge>
  );
}
export function RegistrationFacts({ registration: r }: { registration: Registration }) {
  const observations = r.observations ?? [];
  const fieldLabel: Record<string, string> = {
    name: 'ФИО',
    section: 'Раздел / бригада',
    department: 'Подразделение',
    brigade: 'Бригада',
    position: 'Должность',
    status: 'Статус в источнике',
  };
  const unique = (field: string) => [
    ...new Set(
      observations
        .filter((o) => o.field === field)
        .map((o) => o.rawValue?.trim())
        .filter((v): v is string => Boolean(v)),
    ),
  ];
  const fields = ['department', 'section', 'brigade', 'position', 'status'].map((key) => ({
    key,
    values: unique(key),
  }));
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{r.siteName ?? r.siteId}</Badge>
        <span className="text-ink-2">№ {r.localPersonId}</span>
        <StateBadge state={r.localStatus} />
      </div>
      <p className="font-medium">{r.names.join(' / ') || 'ФИО не получено'}</p>
      <div role="group" aria-label="Сведения регистрации" className="rounded-control bg-subtle px-3 py-2">
        <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-x-3 gap-y-2 text-[13px] max-sm:grid-cols-1 max-sm:gap-y-1">
          {fields.map(({ key, values }) => (
            <div key={key} className="contents">
              <dt className="text-ink-2 max-sm:mt-1">{fieldLabel[key]}</dt>
              <dd className="min-w-0 break-words">
                {values.length ? values.join(' · ') : <span className="text-ink-3">Не указано</span>}
                {values.length > 1 && (
                  <span className="mt-1 block text-xs text-ink-3">
                    Несколько вариантов в источниках; актуальность не установлена
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <p className="text-xs text-ink-3">Последний проход: {dateTime(r.lastEventMs)}</p>
      <details>
        <summary className="min-h-8 cursor-pointer text-xs font-medium text-accent">
          Исходные сведения и история ({observations.length})
        </summary>
        <div className="mt-2 space-y-3 border-l border-line pl-3">
          <p className="break-all text-xs text-ink-3">
            Источник {r.sourceInstanceId} · регистрация {r.id}
          </p>
          {observations.map((o, i) => (
            <div key={o.id ?? i} className="border-b border-line pb-2">
              <p className="text-xs text-ink-2">{fieldLabel[o.field ?? ''] ?? 'Сведения источника'}</p>
              <p className="mt-1 break-words text-[13px]">
                {o.rawValue ?? o.department ?? o.section ?? o.position ?? o.displayName ?? 'Не указано'}
              </p>
              <p className="mt-1 text-xs text-ink-3">
                Получено {dateTime(o.receivedAtMs)}
                {o.exportedAtMs ? ` · выгрузка ${dateTime(o.exportedAtMs)}` : ' · дата выгрузки неизвестна'}
                {o.rowNumber ? ` · строка ${o.rowNumber}` : ''}
              </p>
              <p className="mt-1 break-all text-xs text-ink-3">
                {o.sourceVersion ? `Версия ${o.sourceVersion}` : (o.sourceKey ?? '')}
              </p>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

/** Commit always reuses the preview input and full server revisions; retry never invents a new command key. */
export function PreviewCommit({
  title,
  preview,
  input,
  endpoint,
  method = 'POST',
  onDone,
  onCancel,
  summary,
  blockedReason,
}: {
  title: string;
  preview: Envelope<Preview>;
  input: unknown;
  endpoint: string;
  method?: string;
  onDone: () => void;
  onCancel: () => void;
  summary?: ReactNode;
  blockedReason?: string;
}) {
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());
  const p = preview.data;
  const now = useNow();
  const expired = p.expiresAtMs !== undefined && now > p.expiresAtMs;
  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      await directoryRequest(
        endpoint,
        method,
        command(input, p.expectedRevisions ?? p.revisions ?? preview.revisions, p.previewToken, requestId),
      );
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      title={title}
      onClose={onCancel}
      description="Проверьте состав и последствия перед сохранением."
    >
      <div className="space-y-4">
        {summary && <div className="rounded-control border border-line p-3 text-sm">{summary}</div>}
        <Callout icon={Info}>
          <p>Изменения затронут только справочник и его связи.</p>
          {p.effects && (
            <p className="mt-1">
              Карточек: {p.effects.personIds.length}; регистраций: {p.effects.registrationIds.length}.
            </p>
          )}
          {p.operation_date && <p>Организационная дата: {p.operation_date}</p>}
        </Callout>
        {p.consequences?.map((c) => (
          <p key={c} className="text-sm">
            {c}
          </p>
        ))}
        {p.conflicts?.map((c) => (
          <Callout key={c.code} tone="warn" icon={AlertCircle}>
            {c.message ?? c.code}
          </Callout>
        ))}
        {expired && (
          <Callout tone="warn" icon={AlertCircle}>
            Предпросмотр устарел. Вернитесь к форме и проверьте заново.
          </Callout>
        )}
        {blockedReason && (
          <Callout tone="warn" icon={AlertCircle}>
            {blockedReason}
          </Callout>
        )}
        <ErrorNotice error={error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onCancel}>Вернуться к форме</Button>
          <Button
            variant="primary"
            icon={Check}
            disabled={
              busy || expired || Boolean(p.conflicts?.length) || p.allowed === false || Boolean(blockedReason)
            }
            onClick={() => void commit()}
          >
            {busy ? 'Сохранение…' : 'Подтвердить изменения'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
export function DateFields({
  from,
  to,
  setFrom,
  setTo,
  utc = false,
}: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void; utc?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
      <Field label={utc ? 'Начало периода (UTC)' : 'Дата начала'}>
        <input
          aria-label={utc ? 'Начало периода (UTC)' : 'Дата начала'}
          required
          type={utc ? 'datetime-local' : 'date'}
          className={inputCls}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </Field>
      <Field
        label={utc ? 'Конец периода (UTC), не включая' : 'Дата окончания, не включая — по желанию'}
        hint="Пусто — без ограничения в будущем"
      >
        <input
          aria-label={utc ? 'Конец периода (UTC), не включая' : 'Дата окончания, не включая — по желанию'}
          type={utc ? 'datetime-local' : 'date'}
          className={inputCls}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </Field>
    </div>
  );
}
export function revisionsFor(kind: string, id: string, revision: number): Expected {
  return [{ kind, id, revision }];
}
export function Table({ children, label }: { children: ReactNode; label: string }) {
  return (
    <Card>
      <div className="max-w-full overflow-x-auto">
        <table aria-label={label} className="t-tbl t-tbl-stack w-full text-left text-sm">
          {children}
        </table>
      </div>
    </Card>
  );
}

/** Same component/input/revisions retain the command identity after a lost response. */
export function useDirectoryCommand() {
  const keys = useRef(new Map<string, string>());
  return <T,>(input: T, expectedRevisions: Expected, previewToken?: string) => {
    const key = JSON.stringify({ input, expectedRevisions, previewToken });
    let requestId = keys.current.get(key);
    if (!requestId) {
      requestId = crypto.randomUUID();
      keys.current.set(key, requestId);
    }
    return command(input, expectedRevisions, previewToken, requestId);
  };
}

export function useNow() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function DirectoryExport({
  kind,
  filters,
  snapshotRevision,
  label = 'Экспорт справочника',
}: {
  kind: 'people' | 'brigades';
  filters: Record<string, string>;
  snapshotRevision: string;
  label?: string;
}) {
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ id: string; state: string; expiresAtMs: number } | null>(null);
  const [requestId, setRequestId] = useState<string>(() => crypto.randomUUID());
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await directoryRequest<{ id: string; state: string; expiresAtMs: number }>(
        '/api/personnel/exports',
        'POST',
        command({ kind, filters, expectedSnapshotRevision: snapshotRevision }, [], undefined, requestId),
      );
      setResult(r.data);
      setRequestId(crypto.randomUUID());
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Button disabled={busy} onClick={() => void create()}>
        {busy ? 'Подготовка…' : label}
      </Button>
      {result &&
        (result.state === 'ready' && Date.now() <= result.expiresAtMs ? (
          <a
            className="ml-3 inline-flex min-h-10 items-center text-sm font-medium text-accent"
            href={`/api/personnel/exports/${encodeURIComponent(result.id)}/download`}
            download
          >
            Скачать подготовленный файл
          </a>
        ) : (
          <p role="status" className="text-sm text-ink-2">
            {result.state === 'expired'
              ? 'Срок файла истёк. Подготовьте новый экспорт.'
              : result.state === 'failed'
                ? 'Экспорт завершился ошибкой.'
                : 'Файл подготавливается. Обновите сведения перед загрузкой.'}
          </p>
        ))}
      <ErrorNotice error={error} />
    </div>
  );
}

export const CAPABILITY_LABEL: Record<string, string> = {
  'personnel.read': 'Просмотр персонала',
  'personnel.select': 'Подбор существующих людей',
  'personnel.create': 'Создание карточек',
  'personnel.update': 'Правка выбранных сведений',
  'source_evidence.read': 'Просмотр исходных сведений',
  'identity.review': 'Сверка между площадками',
  'identity.correct': 'Исторический разбор и исправление связей',
  'accounts.person_select': 'Подбор человека для аккаунта',
  'accounts.person_bind': 'Привязка аккаунтов',
  'brigade.read': 'Просмотр бригад',
  'brigade.rename': 'Переименование бригад',
  'brigade.create_own': 'Создание своих бригад',
  'brigade.manage': 'Управление деятельностью бригад',
  'membership.read': 'Просмотр состава',
  'membership.manage': 'Изменение состава',
  'membership.correct_history': 'Исправление истории состава',
  'assignments.manage': 'Управление назначениями и правами',
  'cases.manage': 'Назначение ответственных за случаи',
  'source_mapping.manage': 'Разрешение происхождения источников',
  'jobs.retry': 'Повтор обработки',
  'directory.import': 'Импорт справочников',
  'directory.export': 'Экспорт справочника',
};
export function nameReason(code: string): string {
  const labels: Record<string, string> = {
    order_normalized: 'Учтён порядок частей ФИО',
    insufficient_name: 'Недостаточно частей ФИО',
    complex_name: 'Составное имя требует проверки',
    exact_complex_name: 'Составное имя совпадает',
    compatible_initials: 'Инициалы совместимы',
    relevant_name_version_conflict: 'Есть конфликт между версиями ФИО',
  };
  if (labels[code]) return labels[code];
  const [part, type] = code.split('_');
  const roles: Record<string, string> = { surname: 'Фамилия', given: 'Имя', patronymic: 'Отчество' };
  const kinds: Record<string, string> = {
    exact: 'совпадает',
    initial: 'совместимые инициалы',
    prefix: 'явное сокращение',
    typo: 'возможная опечатка',
    missing: 'не указано в одном источнике',
    conflict: 'противоречие',
  };
  return roles[part ?? ''] && kinds[type ?? '']
    ? `${roles[part ?? '']}: ${kinds[type ?? '']}`
    : 'Дополнительный признак ФИО';
}
